import React, { useState, useEffect, useRef, useContext } from 'react';
import VideoTransitionSlide from '../../components/VideoTransitionSlide';
import { getVideoUrl } from '../../firebase/storage';
import { Box, Spinner, Text, Center, Fade } from '@chakra-ui/react';
import { VideoControlContext } from '../../components/SlideControls';
import { narrationScript } from '../../data/narrationScript';
import { useAudio } from '../../context/AudioContext';

interface JIMIVideoTransitionProps {
  onComplete?: () => void;
  onProgressUpdate?: (progress: number) => void;
}

const JIMIVideoTransition: React.FC<JIMIVideoTransitionProps> = ({ 
  onComplete,
  onProgressUpdate 
}) => {
  const [videoSrc, setVideoSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  
  // Get video control context from SlideControls
  const { isVideoPlaying, toggleVideoPlayback, skipVideo, videoProgress, isVideoSlide } = useContext(VideoControlContext);
  
  // Use the audio context
  const { playAudioForSlide } = useAudio();

  useEffect(() => {
    const loadVideo = async () => {
      try {
        // Try to get the video from Presentation-specific Firebase Storage
        const videoUrl = await getVideoUrl('videos/jimi-transition.mp4');
        setVideoSrc(videoUrl);
        setLoading(false);
      } catch (error) {
        console.error('Error loading video from Firebase:', error);
        // Fallback to local video in the public assets directory
        setVideoSrc('/assets/videos/jimi-transition.mp4');
        setLoading(false);
      }
    };

    loadVideo();

    // Play audio when component mounts, regardless of video state
    const audioTimer = setTimeout(() => {
      console.log('[JIMIVideoTransition] Starting audio playback...');
      playAudioForSlide('video-transition');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  // Control video playback based on context state
  useEffect(() => {
    if (!videoRef.current) return;
    
    if (isVideoPlaying) {
      videoRef.current.play().catch(error => {
        console.error("Video playback error:", error);
      });
    } else {
      videoRef.current.pause();
    }
  }, [isVideoPlaying]);

  // Update progress when video time changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (video.duration) {
        // Calculate progress percentage
        const progress = (video.currentTime / video.duration) * 100;
        // Update the parent component's progress state
        if (typeof window !== 'undefined') {
          // Use a custom event to communicate with StakeholderDeck
          const event = new CustomEvent('videoProgressUpdate', { 
            detail: { progress } 
          });
          window.dispatchEvent(event);
        }
      }
    };

    const handleEnded = () => {
      if (onComplete) {
        onComplete();
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [onComplete]);

  if (loading) {
    return (
      <Center height="100vh" bg="black">
        <Box textAlign="center">
          <Spinner size="xl" color="methodist.blue" mb={4} thickness="4px" />
          <Text 
            color="white" 
            fontSize="lg" 
            fontWeight="medium"
            letterSpacing="wide"
          >
            Preparing Your Wellness Journey
          </Text>
        </Box>
      </Center>
    );
  }

  if (error) {
    return (
      <Center height="100vh" bg="black">
        <Text color="white" fontSize="lg">{error}</Text>
      </Center>
    );
  }

  // Find the video transition narration from narrationScript
  const videoTransitionNarration = narrationScript.find(slide => slide.slideId === 'video-transition');

  return (
    <Fade in={!loading} transition={{ enter: { duration: 0.5 } }}>
      <VideoTransitionSlide
        videoSrc={videoSrc}
        title={videoTransitionNarration?.title || 'JIMI Wellness Platform'}
        subtitle="Your Journey to Wellness Begins Here"
        onComplete={skipVideo || onComplete}
        autoPlay={true}
        loop={false}
        ref={videoRef}
        overlayColor="black"
        accentColor="methodist.blue"
        titleColor="methodist.blue"
        subtitleColor="white"
      />
    </Fade>
  );
};

export default JIMIVideoTransition;
